import {
  dispatchAction,
  reducedStream,
  reducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import {
  clearAndSetLoadedState,
  ClearAndSetLoadedStatePayload,
  clearLoadedState,
  createdItemsProcessed,
  CreatedItemsProcessedPayload,
  deleteLoadedState,
  itemsCreated,
  loadedStatesScopeDataPatched,
  LoadedStatesScopeDataPatchedPayload,
  loadedStateUpdated,
  LoadedStateUpdatedPayload,
  registerLoadedState,
  RegisterLoadedStatePayload,
  setTraversalId,
  SetTraversalIdPayload,
  toggleCollapsedPathActive,
  toggleLoadedStateVisibility,
  ToggleLoadedStateVisibilityPayload,
} from './actions';
import {
  loadedStateOperations,
  LoadedStatesState,
} from './loadedStateOperations';
import { distinctUntilChanged, map, tap } from 'rxjs';
import { CreatedItemsLoadedState, LoadedStateHash } from '@ardoq/api-types';
import { rebuildTraversalAndSearchState } from 'streams/traversals/actions';

const defaultState: LoadedStatesState = { loadedStates: [] };

export const loadedState$ = toPersistentStream<LoadedStatesState>(
  'loadedState$',
  reducedStream<LoadedStatesState>('loadedState$', defaultState, [
    reducer<LoadedStatesState, RegisterLoadedStatePayload>(
      registerLoadedState,
      loadedStateOperations.reduceRegisterLoadedState
    ),
    reducer<LoadedStatesState, ClearAndSetLoadedStatePayload>(
      clearAndSetLoadedState,
      loadedStateOperations.reduceClearAndSetLoadedState
    ),
    reducer<LoadedStatesState, void>(
      clearLoadedState,
      loadedStateOperations.reduceClearLoadedState
    ),
    reducer<LoadedStatesState, SetTraversalIdPayload>(
      setTraversalId,
      loadedStateOperations.reduceSetTraversalId
    ),
    reducer<LoadedStatesState, ToggleLoadedStateVisibilityPayload>(
      toggleLoadedStateVisibility,
      loadedStateOperations.reduceToggleLoadedStateVisibility
    ),
    reducer<LoadedStatesState, LoadedStateHash>(
      deleteLoadedState,
      loadedStateOperations.reduceDeleteLoadedState
    ),
    reducer<LoadedStatesState, LoadedStateUpdatedPayload>(
      loadedStateUpdated,
      loadedStateOperations.replaceExistingLoadedState
    ),
    reducer<LoadedStatesState, LoadedStatesScopeDataPatchedPayload>(
      loadedStatesScopeDataPatched,
      loadedStateOperations.reduceLoadedStatesScopeDataPatched
    ),
    reducer<LoadedStatesState, CreatedItemsLoadedState>(
      itemsCreated,
      loadedStateOperations.reduceUpdateOrCreateLoadedStateOnItemCreated
    ),
    reducer<LoadedStatesState, CreatedItemsProcessedPayload>(
      createdItemsProcessed,
      loadedStateOperations.createdItemsProcessed
    ),
    reducer(
      toggleCollapsedPathActive,
      loadedStateOperations.toggleCollapsedPathActive
    ),
  ]).pipe(
    distinctUntilChanged(),
    tap(state => {
      dispatchAction(
        rebuildTraversalAndSearchState({
          loadedStates: state.loadedStates,
        })
      );
      // Debug info only; window.ARDOQ may not be set yet when this first emits
      if (window.ARDOQ) {
        window.ARDOQ.loadedState = state.loadedStates;
      }
    })
  ),
  { loadedStates: [] }
);

export const isLoadedStateEmpty$ = loadedState$.pipe(
  map(({ loadedStates }) => loadedStates.length === 0),
  distinctUntilChanged()
);

export const isConvertedFromWorkspaceMode$ = loadedState$.pipe(
  map(({ loadedStates }) =>
    loadedStateOperations.isConvertedFromWorkspaceMode(loadedStates)
  ),
  distinctUntilChanged()
);
